import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import { useOrganizationLocation } from "../organizationServices";
import { notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import makeStyles from "@mui/styles/makeStyles";
import OrganizationLocationDialog from "../edit/OrganizationLocationDialog";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const OrganizationLocationCard = ({ id, organizationName }) => {
  const classes = useStyles();
  const [location, setLocation] = useState(null);

  const locationResponse = useOrganizationLocation(id);
  useEffect(() => {
    if (locationResponse.isSuccess && notBlank(locationResponse.data)) {
      setLocation(locationResponse.data);
    }
  }, [locationResponse.isSuccess, locationResponse.data]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    console.log("Handling Edit click...");
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditSave = useCallback(
    async data => {
      await locationResponse.refetch();
      setEditDialogOpen(false);
      // setSaveAlert(true);
    },
    [locationResponse],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <Fragment>
        <CardHeader
          titleTypographyProps={{
            variant: "h6",
            color: "primary",
          }}
          title="Location"
          action={
            <Tooltip title="Edit Location">
              <IconButton onClick={handleEditClick} size="large">
                <EditIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          }
          classes={{ root: classes.header }}
        />
        <CardContent classes={{ root: classes.content }}>
          {locationResponse.isError && (
            <Box padding={5} fontSize="1.6rem" fontStyle="italic">
              No Location set.
            </Box>
          )}
          {locationResponse.isLoading && <PanelLoadingIndicator />}
          {locationResponse.isSuccess && notBlank(location) && (
            <Grid container spacing={3}>
              <Grid item sm={12}>
                <LabeledReadOnly label="Address">
                  <Typography>{location.addressLineOne}</Typography>
                  <Typography>{location.addressLineTwo}</Typography>
                  <Typography>{`${location.locality}, ${location.administrativeArea} ${location.postalCode}`}</Typography>
                  <Typography>{location.country}</Typography>
                </LabeledReadOnly>
              </Grid>
              {notBlank(location.coordinates) && (
                <Fragment>
                  <Grid item sm={6}>
                    <LabeledReadOnly label="Latitude">
                      <Typography>{location.coordinates.latitude}</Typography>
                    </LabeledReadOnly>
                  </Grid>
                  <Grid item sm={12}>
                    <LabeledReadOnly label="Longitude">
                      <Typography>{location.coordinates.longitude}</Typography>
                    </LabeledReadOnly>
                  </Grid>
                </Fragment>
              )}
            </Grid>
          )}
        </CardContent>
      </Fragment>
      {editDialogOpen && (
        <OrganizationLocationDialog
          open={editDialogOpen}
          location={location}
          organizationId={id}
          organizationName={organizationName}
          onCancel={handleEditCancel}
          onSave={handleEditSave}
        />
      )}
    </Card>
  );
};

export default OrganizationLocationCard;
